import { EventEmitter, Injectable, inject } from '@angular/core';
import { ProductConfigurationBaseService } from '@df/module-product-configuration/model/base/product-configuration-base.service';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { PriceService } from '@df/ui/price/price.service';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ITierPrice, Product } from '../product.decorator';
import { CustomerModel } from '@df/session/api/customer.model';

@Injectable()
export class LocalProductConfigurationBaseService extends ProductConfigurationBaseService {
    /**
     * Genera change event
     */
    readonly change$ = new EventEmitter<void>();

    /**
     * Qty unit label
     */
    protected qtyUnit?: string;

    /**
     * Area of 1 piece of product
     */
    private pieceArea?: number;

    /**
     * Flag to determine if sqM can be used for this product
     */
    private _canUseSqM = false;

    /**
     * Flag to determine if unit used is square meter or numeric qty
     */
    private _isSqM = false;

    /**
     * Typed in area
     */
    private area = 0;

    /**
     * services
     */
    private translationService = inject(TranslationService);
    private priceService = inject(PriceService);
    private customerModel = inject(CustomerModel);

    /**
     * Is sqM display used flag
     */
    get canUseSqM() {
        return this._canUseSqM;
    }

    get alternativeQty(): string {
        if (this.isSqM) {
            return `${this.configuration.qty} ${this.qtyUnit}`.trim();
        }
        return `${this.area} m²`;
    }

    get alternativeQtyCopy(): string {
        return this.translationService.transform('equals %', this.configuration.qty);
    }

    get price() {
        return this.configuration.price();
    }

    get orgPrice() {
        return this.configuration.orgPrice();
    }

    get retailPrice() {
        return (this.configuration.product$.value as Product).calcRetailPrice({
            ...this.configuration.toBasketRequest(),
            qty: 1
        });
    }

    get retailPriceInfo(): string {
        return this.preparePriceInfo(this.retailPrice);
    }

    get priceInfo(): string {
        return this.preparePriceInfo(this.price);
    }

    private preparePriceInfo(price: number): string {
        const sqMUnitCopy = this.translationService.transform('per m²');
        const pieceUnitCopy = this.translationService.transform(this.qtyUnit ? 'per %' : 'each', this.qtyUnit);
        const taxPart = this.translationService.transform(this.priceService.showCatalogPricesInclTax ? '(inc VAT)' : '+ VAT');
        const or = this.translationService.transform('or');

        if (!this.canUseSqM) {
            return `${pieceUnitCopy} ${taxPart}`;
        }

        const sqmPrice = this.toSqMPrice(price);
        const sqmPricewithCurrency = sqmPrice ? this.priceService.toPlainString(sqmPrice) : undefined;

        return sqmPricewithCurrency ? `${pieceUnitCopy} ${or} ${sqmPricewithCurrency} ${sqMUnitCopy} ${taxPart}` : '';
    }

    get tierPrices(): ITierPrice[] | undefined {
        return (this.configuration.product$.value as Product)?.getTierPrices(this.customerModel.data.group_id);
    }

    /**
     * Returns qty to display either as numeric qty or area depending on isSqM flag
     */
    get displayQty(): number {
        const qty = this._isSqM ? this.area : this.configuration.qty;
        return qty || 1;
    }
    /**
     * Is sqM display used flag
     */
    get isSqM() {
        return this._isSqM;
    }

    /**
     * Sets numeric qty from input value being either qty or area depending on isSqM flag
     */
    set displayQty(value: number) {
        if (value && typeof value !== 'number') {
            value = Number(value);
        }
        this.area = value ? (this.isSqM ? Math.round(100 * value) / 100 : this.qtyToSqM(value)) : 0;
        this.configuration.qty = this._isSqM ? this.sqMToQty(value) : value;

        this.change$.emit();
    }

    /**
     * Sets flag to determine if unit used is square meter or numeric qty
     */
    set isSqM(value: boolean) {
        this._isSqM = value;
        this.change$.emit();
    }

    /**
     * Init override to sync with product changes
     */
    override init(configuration: ProductConfiguration): void {
        super.init(configuration);
        this.subscriptions.add(
            this.configuration.product$.subscribe(product => {
                const data = product?.data;
                let canUseSqM = false;
                if (data?.show_enhanced_qty_selector) {
                    const width = data.measurement_width;
                    const length = data.measurement_length;
                    canUseSqM = length && width ? true : false;
                    this.pieceArea = canUseSqM ? (length! / 1000) * (width! / 1000) : 0;
                } else {
                    this.pieceArea = 0;
                }
                this.qtyUnit = data?.sunitdesc || '';

                this.displayQty = this.displayQty; // to sync things
                if (this._canUseSqM !== canUseSqM) {
                    this._canUseSqM = canUseSqM;
                }
            })
        );
    }

    toSqMPrice(price: number): number | undefined {
        return this.pieceArea ? Math.round((100 * price) / this.pieceArea!) / 100 : undefined;
    }

    qtyToSqM(qty: number): number {
        return Math.round(100 * qty * this.pieceArea!) / 100;
    }

    private sqMToQty(sqM: number): number {
        return Math.ceil(sqM / this.pieceArea!);
    }
}
